import { TermsChangeSet } from "@/pages/contracts/components/updateTerms/types";

// What state a saved version is in. Three states rather than `isPending`'s two:
// a version that was approved and then cancelled before it ever took effect is
// neither applied nor pending, and reporting it as either misdescribes it.
export enum TermsVersionStatus {
  APPLIED = "APPLIED",
  PENDING = "PENDING",
  WITHDRAWN = "WITHDRAWN"
}

// One entry in the terms history timeline. Every figure is frozen at save time,
// so a version keeps reporting what it was worth after the services it
// referenced were repriced or deleted.
export interface TermsVersionSummary {
  versionId: string;
  // Position in the contract's own sequence; 0 is the initial terms.
  versionIndex: number;
  effectiveDate: string | null;
  // When the next version takes over; null for the terms currently in force.
  effectiveUntil: string | null;
  // What to branch the entry's rendering on. `isPending` below is the older,
  // narrower signal and is still sent, but it cannot tell a withdrawn version
  // apart from an applied one — both report false.
  status: TermsVersionStatus;
  // A future-dated amendment that has not been applied yet. Excludes withdrawn.
  isPending: boolean;
  appliedAt: string | null;
  // Set only on a withdrawn version, which the timeline filters out entirely —
  // so nothing on the list view reads these. The cancellation is surfaced on
  // the version page instead, from the detail payload's own copies below.
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancelledByName: string | null;
  cancellationReason: string | null;
  amendmentReason: string | null;
  createdBy: string | null;
  // Resolved by the list endpoint, so the timeline needs no second call.
  createdByName: string | null;
  createdAt: string | null;
  servicesCount: number | null;
  currency: string | null;
  totalContractValue: number | null;
  totalContractValueLocal: number | null;
  // Against the previous version. Null on index 0, and null when either side
  // has no figure to subtract.
  totalContractValueChange: number | null;
  totalContractValueLocalChange: number | null;
}

export interface TermsVersionListResponse {
  contractId: string;
  versions: TermsVersionSummary[];
}

export interface TermsVersionChangesResponse {
  contractId: string;
  versionId: string;
  versionIndex: number;
  // Null on the initial terms, which has nothing before it.
  previousVersionId: string | null;
  changeSet: TermsChangeSet;
}

// Every money figure on these payloads comes as an account/contract pair — the
// bare name is the account currency, the `*Local` twin the contract's. They are
// a pair, not a fallback chain: `currency` on the payload names the contract's
// currency, so rendering the bare field under it would state an account-currency
// amount in the wrong currency. Read the `*Local` field and let an absent one
// read as absent.
//
// Whether the bare field can even be populated while the local one is null
// depends on the field, which is the part that is easy to get wrong:
//
//   - ACV, ARR and MRR take theirs from `_convert(local)`, which returns null
//     for a null input — so those can never be populated alone.
//   - TCV and CARR/CMRR are summed independently over the line items
//     (`compute_terms_financials`, and `compute_carr_cmrr`: "`carr` sums
//     `total`, `carr_local` sums `total_local`"), so for those the state is
//     reachable and the distinction is load-bearing.
//
// `contractsapi/terms/financials.py` is the source; the backend declines to copy
// a local amount into an account column for the same reason.
//
// The figures as they stood at one version, for the View-terms footer.
export interface TermsVersionFinancials {
  currency: string | null;
  mrr: number | null;
  mrrLocal: number | null;
  cmrr: number | null;
  cmrrLocal: number | null;
  arr: number | null;
  arrLocal: number | null;
  carr: number | null;
  carrLocal: number | null;
  annualContractValue: number | null;
  annualContractValueLocal: number | null;
  totalContractValue: number | null;
  totalContractValueLocal: number | null;
}

export interface TermsVersionItem {
  revenueItemId: string | null;
  itemOrder: number | null;
  serviceItemId: string | null;
  serviceItemName: string | null;
  customDuration: number | null;
  serviceStartDate: string | null;
  serviceEndDate: string | null;
  customServiceDuration: number | null;
  autoRenewal: boolean | null;
  customOptOutDate: string | null;
  quantity: number | null;
  pricePerUnit: number | null;
  isDiscountPercentage: boolean | null;
  discountAmount: number | null;
  customBillingFrequency: number | null;
  billingStartDate: string | null;
  eqPaymentsNumOfPayments: number | null;
  eqPaymentsBillingFrequency: number | null;
  total: number | null;
  totalLocal: number | null;
  // The bundle this line was in at this version; the totals above are after
  // its share of the bundle discount, which these record.
  contractBundleId?: string | null;
  bundleDiscount?: number | null;
  bundleDiscountLocal?: number | null;
}

// A bundle as it stood at one version; its lines name it by contractBundleId.
export interface TermsVersionBundle {
  contractBundleId: string | null;
  itemOrder: number | null;
  name: string | null;
  servicesStartDate: string | null;
  isDiscountPercentage: boolean | null;
  discountAmount: number | null;
}

export interface TermsVersionDetailResponse {
  contractId: string;
  versionId: string;
  versionIndex: number;
  effectiveDate: string | null;
  appliedAt: string | null;
  status: TermsVersionStatus;
  isPending: boolean;
  // No `cancelledByName` twin here, unlike the list summary — the detail
  // endpoint resolves no display name, so the page names only the date.
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  amendmentReason: string | null;
  createdBy: string | null;
  createdAt: string | null;
  items: TermsVersionItem[];
  // Empty for versions saved before bundles existed.
  bundles?: TermsVersionBundle[];
  financials: TermsVersionFinancials;
}
